import React from 'react';
import { Check, X } from 'lucide-react';
import './DemoReviewModal.css';

function DemoReviewModal({ restroom, onClose, onSignUp }) {
  if (!restroom) return null;

  // Helper functions (copied from DemoMap)
  const getRatingEmoji = (rating) => {
    if (rating >= 4.5) return '🌟';
    if (rating >= 3.5) return '✨';
    if (rating >= 2.5) return '💫';
    if (rating >= 1.5) return '⭐';
    return '💩';
  };

  const formatRating = (rating) => {
    return rating.toFixed(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getSampleReview = (restroom) => {
    const rating = restroom.rating || 3;
    
    const sampleReviews = {
      5: [
        {
          id: 'sample-5-1',
          username: 'Clean Freak',
          rating: 5,
          cleanlinessRating: 5,
          accessibilityRating: 5,
          comment: 'Absolutely pristine! Spotless floors, well-stocked supplies, and smelled fresh. The attendant keeps it immaculate.',
          createdAt: '2024-01-15T10:30:00Z',
          hasBidet: true,
          hasChangingTable: true,
          hasFeminineProducts: true,
          hasAirDryer: true,
          hasHandicapAccessible: true
        }
      ],
      4: [
        {
          id: 'sample-4-1',
          username: 'Regular User',
          rating: 4,
          cleanlinessRating: 4,
          accessibilityRating: 4,
          comment: 'Very clean and well-maintained. Everything you need is available. Minor wear on some fixtures but still excellent.',
          createdAt: '2024-01-10T14:20:00Z',
          hasBidet: false,
          hasChangingTable: true,
          hasFeminineProducts: true,
          hasAirDryer: true,
          hasHandicapAccessible: true
        }
      ],
      3: [
        {
          id: 'sample-3-1',
          username: 'Average Joe',
          rating: 3,
          cleanlinessRating: 3,
          accessibilityRating: 3,
          comment: 'Decent facilities. Gets the job done but could use more frequent cleaning. Basic amenities available.',
          createdAt: '2024-01-05T09:15:00Z',
          hasBidet: false,
          hasChangingTable: false,
          hasFeminineProducts: false,
          hasAirDryer: true,
          hasHandicapAccessible: true
        }
      ],
      2: [
        {
          id: 'sample-2-1',
          username: 'Critical Reviewer',
          rating: 2,
          cleanlinessRating: 2,
          accessibilityRating: 2,
          comment: 'Not the best experience. Floors needed attention and some supplies were empty. Functional but lacking.',
          createdAt: '2024-01-02T16:45:00Z',
          hasBidet: false,
          hasChangingTable: false,
          hasFeminineProducts: false,
          hasAirDryer: false,
          hasHandicapAccessible: true
        }
      ],
      1: [
        {
          id: 'sample-1-1',
          username: 'Disappointed',
          rating: 1,
          cleanlinessRating: 1,
          accessibilityRating: 1,
          comment: 'Quite unpleasant. Poor maintenance, unpleasant odors, and multiple issues. Would avoid if possible.',
          createdAt: '2023-12-28T11:30:00Z',
          hasBidet: false,
          hasChangingTable: false,
          hasFeminineProducts: false,
          hasAirDryer: false,
          hasHandicapAccessible: false
        }
      ]
    };

    const ratingCategory = Math.round(rating);
    const reviews = sampleReviews[ratingCategory] || sampleReviews[3];
    return reviews[0];
  };

  const review = getSampleReview(restroom);

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal" onClick={(e) => e.stopPropagation()}>
        {/* Review Header */}
        <div className="review-modal-header">
          <div className="review-modal-title">
            <h2>{restroom.name}</h2>
            <div className="review-modal-rating">
              {getRatingEmoji(restroom.rating)}
              <span className="rating-number">{formatRating(restroom.rating)}</span>
              <span className="review-count">({restroom.reviews?.length || 18} reviews)</span>
            </div>
          </div>
          <button className="review-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Review Content */}
        <div className="review-modal-content">
          {/* User Info */}
          <div className="review-user-info">
            <div className="user-avatar">
              {review.username.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <div className="username-row">
                <div className="username">{review.username}</div>
                <div className="rating-emojis">{'💩'.repeat(review.rating)}</div>
              </div>
              <div className="review-date">{formatDate(review.createdAt)}</div>
            </div>
          </div>

          {/* Comment */}
          <p className="review-comment">
            {review.comment}
          </p>

          {/* Amenities */}
          <div className="review-amenities">
            <div className="amenities-list">
              {review.hasHandicapAccessible && (
                <span className="amenity-tag">Accessible</span>
              )}
              {review.hasChangingTable && (
                <span className="amenity-tag">Family Friendly</span>
              )}
              {review.hasChangingTable && (
                <span className="amenity-tag">Changing Table</span>
              )}
              {review.hasHandicapAccessible && (
                <span className="amenity-tag">Clean</span>
              )}
            </div>
          </div>

          {/* Unlock Section */}
          <div className="unlock-section">
            <h3>Unlock more from this community</h3>
            <div className="unlock-features">
              <div className="unlock-feature">
                <Check size={16} className="check-icon" />
                <span>More reviews</span>
              </div>
              <div className="unlock-feature">
                <Check size={16} className="check-icon" />
                <span>Accessibility details</span>
              </div>
              <div className="unlock-feature">
                <Check size={16} className="check-icon" />
                <span>Toilet paper status</span>
              </div>
              <div className="unlock-feature">
                <Check size={16} className="check-icon" />
                <span>Nearby alternatives</span>
              </div>
            </div>
            <button className="unlock-button" onClick={onSignUp}>
              Unlock All Reviews
            </button>
            <div className="login-link">
              Already have an account? <button onClick={() => window.location.href = '/auth'}>Login here</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DemoReviewModal;
